import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAccountPost, AppRawDone, AppRawOptions } from "./AppAccountPost";
import { AppChatArchiveWho } from "./AppChatArchiveRules";
import { IAppChatArchiveCall, AppChatArchiveAsk } from "./AppChatArchive";
import { AppChatListPage, AppChatListRules } from "./AppChatListRules";
import { AppSignUpApi } from "./AppSignUpApi";
import { IAppLiveClock, AppLiveWindowClock } from "./AppLiveFeed";

export interface AppChatListHttp {
    new(Url: string, Body: string, Done: AppRawDone, Options: AppRawOptions): IAppChatArchiveCall;
}

/** One selected list, bounded cursor reads, and visible read-only refreshes. No per-contact requests or writes. */
export class AppChatListRead {
    public View: string = "all";
    public Note: string = "";
    public Ready: boolean = true;
    public Loading: boolean = false;
    public Ids: Set<string> = new Set();
    private Ask: AppChatArchiveAsk;
    private Changed: () => void;
    private Http: AppChatListHttp;
    private Clock: IAppLiveClock;
    private Active: boolean = false;
    private Version: number = 0;
    private Call: IAppChatArchiveCall | null = null;
    private Scope: AppChatArchiveWho = new AppChatArchiveWho();
    private Collected: Set<string> = new Set();
    private Cursors: Set<string> = new Set();
    private Pages: number = 0;
    private Poll: number = 0;

    public constructor(Ask: AppChatArchiveAsk, Changed: () => void, Http: AppChatListHttp = AppAccountPost, Clock: IAppLiveClock = new AppLiveWindowClock()) {
        this.Ask = Ask; this.Changed = Changed; this.Http = Http; this.Clock = Clock;
    }

    public Load = (View: string, Quiet: boolean = false): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppChatListRules.Views.indexOf(View) < 0) { return; }
            this.Cancel(); this.View = View;
            const Who: AppChatArchiveWho = this.Ask();
            if (this.SameScope(Who) === false || Quiet === false) { this.Ids = new Set(); this.Ready = View === "all"; }
            this.Scope = Object.assign(new AppChatArchiveWho(), Who); this.Collected = new Set(); this.Cursors = new Set(); this.Pages = 0;
            this.Note = ""; this.Loading = View !== "all";
            if (View === "all") { this.Changed(); return; }
            this.Note = "Checking " + View + " chats…"; this.Changed();
            if (this.Active === false) { return; }
            this.Next("");
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.Load"); this.Cancel(); }
    };

    private SameScope = (Who: AppChatArchiveWho): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try { return Who.Number === this.Scope.Number && Who.Key === this.Scope.Key && Who.ServiceUrl === this.Scope.ServiceUrl; }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.SameScope"); return false; }
    };

    public ScopeMatches = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try { return this.SameScope(this.Ask()); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.ScopeMatches"); return false; }
    };

    private Next = (Cursor: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Base: string = AppSignUpApi.UrlFor(this.Scope.ServiceUrl, "/chat/settings");
            if (Base.length === 0 || this.Scope.Key.length === 0 || this.Scope.Number.length === 0) {
                this.Failed("Sign in again to check these chat settings."); return;
            }
            this.Pages++;
            if (this.Pages > 50 || this.Cursors.has(Cursor) === true) { this.Failed("This list is incomplete. Choose the view again to retry."); return; }
            this.Cursors.add(Cursor); this.Version++;
            let Url: string = Base + "?number=" + encodeURIComponent(this.Scope.Number) + "&view=" + this.View;
            if (Cursor.length > 0) { Url += "&cursor=" + encodeURIComponent(Cursor); }
            const Options: AppRawOptions = new AppRawOptions(); Options.Method = "GET"; Options.TimeoutMs = 10000;
            Options.Headers.Authorization = "Bearer " + this.Scope.Key;
            this.Call = new this.Http(Url, "", new AppChatListAnswer(this, this.Version).Heard, Options);
            this.Call.Send();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.Next"); this.Cancel(); }
    };

    public Answer = (Version: number, Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Version !== this.Version || this.Active === false || this.Call === null) { return; }
            this.Call = null;
            if (this.SameScope(this.Ask()) === false) { this.Cancel(); this.Ids = new Set(); this.Ready = false; return; }
            const Page: AppChatListPage = AppChatListRules.Parse(Status, Text, this.View);
            if (Page.Ok === false) { this.Failed(Page.Why); return; }
            for (const Id of Page.Ids) { this.Collected.add(Id); }
            if (Page.Cursor.length > 0) { this.Next(Page.Cursor); return; }
            this.Ids = this.Collected; this.Ready = true; this.Loading = false;
            this.Note = "Starred chats are favourites saved in WACommander.";
            if (this.View === "archived") { this.Note = "Only confirmed WhatsApp archives are shown; chats with unavailable state may be missing."; }
            if (Page.Complete === false) { this.Note = "This list is incomplete. Choose the view again to retry."; }
            this.Changed(); this.Schedule();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.Answer"); this.Cancel(); }
    };

    private Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Note = Why; this.Loading = false;
            if (this.Ready === true && this.Ids.size > 0) { this.Note += " Showing the last checked list."; }
            this.Changed(); this.Schedule();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.Failed"); }
    };

    private Schedule = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { if (this.Active === true && this.View !== "all") { this.Poll = this.Clock.Later(10000, this.Refresh); } }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.Schedule"); }
    };

    private Refresh = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Poll = 0; if (this.Active === true) { this.Load(this.View, true); } }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.Refresh"); }
    };

    public Visible = (Active: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Active === true && this.SameScope(this.Ask()) === false) { this.Active = true; this.Load(this.View); return; }
            if (this.Active === Active) { return; }
            this.Active = Active;
            if (Active === true) { this.Load(this.View, true); }
            else { this.Cancel(); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.Visible"); }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: admitted transport/timer cleanup and generation invalidation remain ungated. */
    private Cancel = (): void => {
        try {
            this.Version++;
            this.Loading = false;
            if (this.Call !== null) { this.Call.Abort(); this.Call = null; }
            if (this.Poll !== 0) { this.Clock.Cancel(this.Poll); this.Poll = 0; }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListRead.Cancel"); }
    };
}

class AppChatListAnswer {
    private Owner: AppChatListRead;
    private Version: number;
    public constructor(Owner: AppChatListRead, Version: number) { this.Owner = Owner; this.Version = Version; }
    public Heard = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Owner.Answer(this.Version, Status, Text); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatListAnswer.Heard"); }
    };
}
